import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import Select from 'react-select'; // Import react-select
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function Ledger() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerId, setCustomer] = useState('');
    const { isAuthenticated, companyId, companyName, branchId, permissions, token, logout } = useAuth();
    const [reportGeneratedTime, setReportGeneratedTime] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());

    }, [isAuthenticated, companyId, token, logout]);

    useEffect(() => {
        fetchCustomerData();
        fetchConsigneeData();
    }, []);

    //FOR GET Customer  Details FROM API
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const fetchCustomerData = () => {
        fetchData('PaymentAndReceiptApi', `/Customer/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                // Transform the data into the format that react-select expects (label and value)                
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.Id,
                }));
                setCustomerOptions(options);

            })
            .catch((error) => {
                console.error('Error fetching Customer data:', error);
            });
    };

    const [ConsigneeOptions, setConsigneeOptions] = useState([]);
    const [selectedConsignee, setSelectedConsignee] = useState(null);
    //FOR GET Consignee  Details FROM API
    const fetchConsigneeData = () => {
        fetchData('PaymentAndReceiptApi', `/Consignee/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                // Transform the data into the format that react-select expects (label and value)                
                const options = data.map((Consignee) => ({
                    label: Consignee.Name,
                    value: Consignee.Id,
                }));
                setConsigneeOptions(options);

            })
            .catch((error) => {
                console.error('Error fetching Consignee data:', error);
            });
    };

    const handleCustomerSelect = (selectedOption) => {
        setSelectedCustomer(selectedOption);
        if (!selectedOption) return;
    };

    const handleConsigneeChange = (selectedOption) => {
        setSelectedConsignee(selectedOption);
        if (!selectedOption) return;
    };



    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const fetchTableData = async () => {
        const now = new Date();
        const formattedDateTime =
            String(now.getDate()).padStart(2, '0') + '-' +
            String(now.getMonth() + 1).padStart(2, '0') + '-' +
            now.getFullYear() + ' ' +
            now.toLocaleTimeString(); // e.g., 10:45:23 AM

        setReportGeneratedTime(formattedDateTime);

        setDataItem([])
        setLoading(true);
        try {
            const customerid = selectedCustomer?.value ?? 0;
            const consigneeid = selectedConsignee?.value ?? 0;
            const response = await fetchData('ReportApi', `/Ledger/${fromDate}/${toDate}/${companyId}/${customerid}/${consigneeid}`, token);
            if (response.responseCode === '1111') {
                setDataItem(response.response_data);

            }
        } catch (error) {
            console.error('Error fetching Ledger Details:', error);
        } finally {
            setLoading(false); // ✅ Hide loading screen always
        }

    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFromDate(selectedDate);
    };

    // Similar function for toDate
    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);;
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${day}/${month}/${year}`;

        return formattedDate;
    };


    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Ledger');
        worksheet.views = [
            { state: 'frozen', xSplit: 0, ySplit: 2 }
        ];
        let bal = 0;

        const computeBalance = (data) => {
            if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
                if (data.debit) bal += Math.abs(data.debit);
                if (data.credit) bal -= Math.abs(data.credit);
            }

            if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
                return bal > 0 ? `${bal.toFixed(2)} Dr` : `${Math.abs(bal).toFixed(2)} Cr`;
            }
            return '';
        };

        const customerLabel = `${selectedCustomer?.label || ""}-${selectedConsignee?.label || ""}`;
        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        };

        // Header Title Row
        worksheet.mergeCells('A1:E1');
        const headerCell = worksheet.getCell('A1');
        headerCell.value = `${companyName} - Ledger for ${customerLabel} From: ${formatDate(fromDate)} To: ${formatDate(toDate)} | Generated On: ${formattedDateTime}`;
        headerCell.font = { bold: true, size: 10 };
        headerCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

        // Column Headers
        const headerRow = worksheet.addRow(['Date', 'Description', 'Debit', 'Credit', 'Balance']);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = thinBorder;
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD9EAD3' }, // Light green
            };
        });

        // Data Rows
        filteredDataItem.forEach((rowData) => {
            let rowValues;

            if (rowData.description1 === 'Opening Balance' || rowData.description1 === 'Closing Balance' || rowData.description1 === 'Total') {
                rowValues = [
                    '',
                    rowData.description1,
                    rowData.debit != null ? rowData.debit.toFixed(3) : '',
                    rowData.credit != null ? rowData.credit.toFixed(3) : '',
                    rowData.description1 === 'Opening Balance' ? computeBalance(rowData) : ''
                ];
            } else {
                rowValues = [
                    formatDateForInput(rowData.date1),
                    rowData.description1,
                    rowData.debit != null ? rowData.debit.toFixed(3) : '',
                    rowData.credit != null ? rowData.credit.toFixed(3) : '',
                    computeBalance(rowData)
                ];
            }

            const row = worksheet.addRow(rowValues);

            if (rowData.description1 === 'Closing Balance' || rowData.description1 === 'Total') {
                row.eachCell((cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFF4CCCC' }, // Light pink
                    };
                    cell.font = { bold: true };
                    cell.border = thinBorder;
                    cell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true };
                });
            } else {
                row.eachCell((cell, colNumber) => {
                    if ([3, 4, 5].includes(colNumber)) {
                        cell.alignment = { horizontal: 'right' };
                        cell.numFmt = '#,##0.00';
                    } else {
                        cell.alignment = { horizontal: 'left' };
                    }
                    cell.border = thinBorder;
                });
            }
        });

        worksheet.columns.forEach((column) => {
            column.width = 20;
        });

        // Export to file
        const buffer = await workbook.xlsx.writeBuffer();
        const sanitizedCustomerLabel = customerLabel.replace(/[^\w]/g, '_');
        saveAs(
            new Blob([buffer]),
            `Ledger_${sanitizedCustomerLabel}_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`
        );
    };


    let copbal = 0; // Cumulative opening balance for credit
    let dopbal = 0; // Cumulative opening balance for debit
    let bal = 0;    // Running balance

    const computeBalance = (data) => {

        if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
            // Add debit to balance
            if (data.debit) {
                bal += Math.abs(data.debit);
            }
            // Subtract credit from balance
            if (data.credit) {
                bal -= Math.abs(data.credit);
            }
        }

        // Return balance string with "Dr" or "Cr"
        if (data.description1 !== 'Closing Balance' && data.description1 !== 'Total') {
            return bal > 0 ? `${bal.toFixed(3)}  Dr` : `${Math.abs(bal).toFixed(3)}  Cr`;
        }
        return '';
    };




    return (

        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div className="loader"></div>
                    <span style={{ color: 'white', marginTop: '10px', fontSize: '14px' }}>
                        Loading...
                    </span>
                </div>
            )}
            <h6 className="uppercase tracking-wide text-black font-serif flex-grow" style={{
                fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif',
                color: 'black',
                marginTop: '10px',
            }}>
                Ledger
            </h6>
            {reportGeneratedTime && (
                <div className="text-sm text-gray-600" style={{ marginTop: '5px', marginRight: '100px' }}>
                    Report Generated: {reportGeneratedTime}
                </div>
            )}
            <div className="flex items-center gap-4 mb-4 ml-1">
                {/* From Date */}
                <div className="flex items-center gap-2">
                    <label className="uppercase text-black text-xs whitespace-nowrap">From </label>
                    <input
                        className="shadow border rounded py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring focus:ring-gray-400"
                        type="date"
                        value={fromDate}
                        onChange={handleFromDateChange}
                    />
                </div>

                {/* To Date */}
                <div className="flex items-center gap-2">
                    <label className="uppercase text-black text-xs whitespace-nowrap">To </label>
                    <input
                        className="shadow border rounded py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring focus:ring-gray-400"
                        type="date"
                        value={toDate}
                        onChange={handleToDateChange}
                    />
                </div>

                {/* Customer */}
                <div className="flex items-center gap-2 flex-1">
                    <label className="uppercase text-black text-xs whitespace-nowrap">Customer</label>
                    <Select
                        value={selectedCustomer}
                        onChange={handleCustomerSelect}
                        options={customerOptions}
                        placeholder="Select"
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "30px",   // same as your inputs
                                height: "30px",
                                fontSize: "14px",
                                width: 270, // ⬅️ increase width here
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "30px",      // keeps text aligned properly
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "30px",      // makes dropdown arrow align
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                {/* Consignee */}
                <div className="flex items-center gap-2 flex-1">
                    <label className="uppercase text-black text-xs whitespace-nowrap">Consignee</label>
                    <Select
                        value={selectedConsignee}
                        onChange={handleConsigneeChange}
                        options={ConsigneeOptions}
                        placeholder="Select"
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "30px",   // same as your inputs
                                height: "30px",
                                fontSize: "14px",
                                width: 270, // ⬅️ increase width here
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "30px",      // keeps text aligned properly
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "30px",      // makes dropdown arrow align
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                {/* Buttons */}
                <Button
                    className="h-8 rounded-full px-4 text-white"
                    style={{
                        background: "linear-gradient(to right, #A8A196,#A8A196)",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                    }}
                    onClick={fetchTableData}
                >
                    Refresh
                </Button>

                <Button
                    className="h-8 rounded-full px-4 text-white"
                    style={{
                        background: "linear-gradient(to right, #3F4F44,#3F4F44)",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                    }}
                    onClick={handleExportExcel}
                >
                    Excel
                </Button>
            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '500px', overflowY: 'auto' }}>

                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#102E50' }}>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '12px', color: 'whitesmoke', padding: '4px', fontWeight: 'bold', textTransform: 'uppercase' }}>Date</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '12px', color: 'whitesmoke', padding: '4px', fontWeight: 'bold', textTransform: 'uppercase' }}>Description</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '12px', color: 'whitesmoke', textAlign: 'right', padding: '4px', fontWeight: 'bold', textTransform: 'uppercase' }}>Debit</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '12px', color: 'whitesmoke', textAlign: 'right', padding: '4px', fontWeight: 'bold', textTransform: 'uppercase' }}>Credit</TableCell>
                                <TableCell sx={{ backgroundColor: '#102E50', borderBottom: '1px solid #ddd', fontSize: '12px', color: 'whitesmoke', textAlign: 'right', padding: '4px', fontWeight: 'bold', textTransform: 'uppercase' }}>Balance</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredDataItem.map((data, index) => (
                                <TableRow key={data.id} sx={data.description1 === 'Opening Balance' || data.description1 === 'Closing Balance' || data.description1 === 'Total' ? { backgroundColor: '#f0f0f0' } : null} style={{ textTransform: 'uppercase', fontSize: '12.5px' }}>

                                    {data.description === 'Opening Balance' || data.description1 === 'Closing Balance' || data.description1 === 'Total' ? (

                                        <TableCell sx={{ padding: '5px', fontWeight: 'bolder', fontSize: '11.5px' }}></TableCell>
                                    ) : (
                                        <TableCell sx={{ padding: '5px', fontSize: '12.5px' }}>{formatDate(data.date1)}</TableCell>
                                    )}

                                    <TableCell
                                        sx={{
                                            //fontSize: '15px',
                                            height: '30px',
                                            padding: '7px',
                                            fontSize: '12.5px',
                                            fontWeight:
                                                data.description1 === 'Opening Balance' ||
                                                    data.description1 === 'Closing Balance' ||
                                                    data.description1 === 'Total'
                                                    ? 'bolder'
                                                    : 'normal',
                                        }}
                                    >
                                        {data.tbl1 === 'Consignment' ? (
                                            <a
                                                href={`/InvoiceDetails/${data.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: 'darkgreen' }}
                                            >
                                                {data.description1}
                                            </a>
                                        )
                                            : data.tbl1 === 'Receipt UPI' ? (
                                                <a
                                                    href={`/PaymentAndReceiptDetails/${data.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#604652' }}
                                                >
                                                    {data.description1}
                                                </a>
                                            )
                                                : data.tbl1 === 'Receipt Cheque' ? (
                                                    <a
                                                        href={`/PaymentAndReceiptDetails/${data.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#604652' }}
                                                    >
                                                        {data.description1}
                                                    </a>
                                                )
                                                    : data.tbl1 === 'Receipt Cash' ? (
                                                        <a
                                                            href={`/PaymentAndReceiptDetails/${data.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: '#604652' }}
                                                        >
                                                            {data.description1}
                                                        </a>

                                                    )
                                                        : data.tbl1 === 'JournalCustomer1' ? (
                                                            <a
                                                                href={`/JournalEntryDetails/${data.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: 'darkorchid' }}
                                                            >
                                                                {data.description1}
                                                            </a>
                                                        )
                                                            : data.tbl1 === 'JournalCustomer2' ? (
                                                                <a
                                                                    href={`/JournalEntryDetails/${data.id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ color: 'darkorchid' }}
                                                                >
                                                                    {data.description1}
                                                                </a>
                                                            )
                                                                : data.tbl1 === 'JournalSupplier1' ? (
                                                                    <a
                                                                        href={`/JournalEntryDetails/${data.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ color: 'darkslategray' }}
                                                                    >
                                                                        {data.description1}
                                                                    </a>

                                                                )
                                                                    : data.tbl1 === 'JournalSupplier2' ? (
                                                                        <a
                                                                            href={`/JournalEntryDetails/${data.id}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{ color: 'darkslategray' }}
                                                                        >
                                                                            {data.description1}
                                                                        </a>
                                                                    )
                                                                        : data.tbl1 === 'JournalBank1' ? (
                                                                            <a
                                                                                href={`/JournalEntryDetails/${data.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{ color: 'darkmagenta' }}
                                                                            >
                                                                                {data.description1}
                                                                            </a>
                                                                        )
                                                                            : data.tbl1 === 'JournalBank' ? (
                                                                                <a
                                                                                    href={`/JournalEntryDetails/${data.id}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    style={{ color: 'darkmagenta' }}
                                                                                >
                                                                                    {data.description1}
                                                                                </a>
                                                                            )
                                                                                : data.tbl1 === 'JournalAccountHead' ? (
                                                                                    <a
                                                                                        href={`/JournalEntryDetails/${data.id}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        style={{ color: 'darkmagenta' }}
                                                                                    >
                                                                                        {data.description1}
                                                                                    </a>
                                                                                )

                                                                                    : data.tbl1 === 'Payment Cash' ? (
                                                                                        <a
                                                                                            href={`/PaymentAndReceiptDetails/${data.id}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            style={{ color: 'darkviolet' }}
                                                                                        >
                                                                                            {data.description1}
                                                                                        </a>
                                                                                    )
                                                                                        : data.tbl1 === 'Payment Cheque' ? (
                                                                                            <a
                                                                                                href={`/PaymentAndReceiptDetails/${data.id}`}
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                                style={{ color: 'darkviolet' }}
                                                                                            >
                                                                                                {data.description1}
                                                                                            </a>
                                                                                        )
                                                                                            : data.tbl1 === 'Payment UPI' ? (
                                                                                                <a
                                                                                                    href={`/PaymentAndReceiptDetails/${data.id}`}
                                                                                                    target="_blank"
                                                                                                    rel="noopener noreferrer"
                                                                                                    style={{ color: 'darkviolet' }}
                                                                                                >
                                                                                                    {data.description1}
                                                                                                </a>
                                                                                            )
                                                                                                : data.tbl1 === 'Purchase' ? (
                                                                                                    <a
                                                                                                        href={`/PurchaseDetails/${data.id}`}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                        style={{ color: 'darkcyan' }}
                                                                                                    >
                                                                                                        {data.description1}
                                                                                                    </a>
                                                                                                )
                                                                                                    : data.tbl1 === 'PurchaseReturn' ? (
                                                                                                        <a
                                                                                                            href={`/PurchaseReturnDetails/${data.id}`}
                                                                                                            target="_blank"
                                                                                                            rel="noopener noreferrer"
                                                                                                            style={{ color: 'red' }}
                                                                                                        >
                                                                                                            {data.description1}
                                                                                                        </a>

                                                                                                    ) : (
                                                                                                        data.description1
                                                                                                    )}
                                    </TableCell>

                                    <TableCell sx={{ fontSize: '12.5px', height: '30px', padding: '5px', textAlign: 'right', fontWeight: data.description1 === 'Opening Balance' || data.description1 === 'Closing Balance' || data.description1 === 'Total' ? 'bolder' : 'normal' }}> {data.debit != null ? data.debit.toFixed(3) : ''}</TableCell>
                                    <TableCell sx={{ fontSize: '12.5px', height: '30px', padding: '5px', textAlign: 'right', fontWeight: data.description1 === 'Opening Balance' || data.description1 === 'Closing Balance' || data.description1 === 'Total' ? 'bolder' : 'normal' }}> {data.credit != null ? data.credit.toFixed(3) : ''}</TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: '12.5px',
                                            height: '30px',
                                            padding: '5px',
                                            textAlign: 'right',
                                            width: '150px',
                                            fontWeight: 'bolder',
                                        }}
                                    >
                                        {computeBalance(data)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>



            </div>

            <div style={{
                position: 'fixed',
                bottom: '12px',
                right: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.3rem 0.65rem',
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '14px',
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
                fontFamily: 'serif',
                fontWeight: 'bold',
                color: '#2c3e50',
                fontSize: '0.7rem',
                backdropFilter: 'blur(10px)'
            }}>
                <CopyrightIcon />
                <b>EFFTABS SOLUTIONS LLP</b>
            </div>
        </div>
    );
}

export default Ledger;
